import { useRect } from '@reach/rect';
import {
  type CSSProperties,
  memo,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { usePlayerEventListener, usePlayerSeekChange, usePlayerStore } from '@air/component-player';
import { Tooltip } from '@air/primitive-tooltip';
import { formatDuration } from '@air/utilities';

import { useAnnotationV2Store } from '~/components/AnnotationsV2/providers/AnnotationV2Provider';
import { type CommentRangeValue } from '~/components/v2/components/asset-modal/visualizer/video/getCommentRangePercentStyle';
import {
  BOTH_CAPS_PX,
  CAP_WIDTH_PX,
  CLEAR_BUTTON_CLEARANCE_PX,
  DRAG_SETTLE_BACKSTOP_MS,
  PERCENTAGE_MULTIPLIER,
  SEEK_THROTTLE_MS,
} from '~/components/v2/components/asset-modal/visualizer/video/VideoCommentRangeSelector/constants';
import { type CommentRangeClearButtonSide } from '~/components/v2/components/asset-modal/visualizer/video/VideoCommentRangeSelector/types';
import { VideoCommentRangeSelectorClearButton } from '~/components/v2/components/asset-modal/visualizer/video/VideoCommentRangeSelector/VideoCommentRangeSelectorClearButton';
import { VideoCommentRangeSelectorHandle } from '~/components/v2/components/asset-modal/visualizer/video/VideoCommentRangeSelector/VideoCommentRangeSelectorHandle';

type VideoCommentRangeSelectorProps = {
  onDragActiveChange?: (isDragging: boolean) => void;
};

export const VideoCommentRangeSelector = memo(({ onDragActiveChange }: VideoCommentRangeSelectorProps) => {
  const duration = usePlayerStore((state) => state.duration);
  const playing = usePlayerStore((state) => state.playing);
  const seeking = usePlayerStore((state) => state.seeking);
  const currentTime = usePlayerStore((state) => state.currentTime);
  const videoElement = usePlayerStore((state) => state.videoElement);
  const setCurrentTime = usePlayerStore((state) => state.setCurrentTime);
  const { onPlayerSeekStart, onPlayerSeekEnd } = usePlayerSeekChange();
  const annotationCount = useAnnotationV2Store((state) => state.annotations.length);
  const commentRange = useAnnotationV2Store((state) => state.commentRange);
  const setCommentRange = useAnnotationV2Store((state) => state.setCommentRange);
  const setCommentRangeEnabled = useAnnotationV2Store((state) => state.setCommentRangeEnabled);
  const focusCommentInput = useAnnotationV2Store((state) => state.focusCommentInput);
  // A null useRef initializer makes the missing-node guard look redundant. React's ref prop still wants `current: T | null`.
  const containerRef = useRef<HTMLDivElement>();
  const containerRect = useRect(containerRef);
  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node ?? undefined;
  }, []);
  const isDraggingRef = useRef<boolean>();
  const isPointerDownRef = useRef<boolean>();
  const settleClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>();
  const wasPlayingRef = useRef(playing);
  const activeBodyDragCleanupRef = useRef<(() => void) | null>(null);
  const isSelectorActive = !!commentRange && duration > 0;

  const handleDragStart = useCallback(() => {
    isDraggingRef.current = true;
    isPointerDownRef.current = true;
    onDragActiveChange?.(true);

    if (settleClearTimeoutRef.current) {
      clearTimeout(settleClearTimeoutRef.current);
      settleClearTimeoutRef.current = null;
    }
  }, [onDragActiveChange]);

  const handleDragEnd = useCallback(
    (range: CommentRangeValue) => {
      isPointerDownRef.current = false;

      settleClearTimeoutRef.current = setTimeout(() => {
        isDraggingRef.current = false;
        settleClearTimeoutRef.current = null;
        onDragActiveChange?.(false);
      }, DRAG_SETTLE_BACKSTOP_MS);

      if (range.startTime !== range.endTime) {
        focusCommentInput(annotationCount > 0 ? 'annotation' : 'panel');
      }
    },
    [annotationCount, focusCommentInput, onDragActiveChange],
  );

  const handleSeeked = useCallback(() => {
    if (isPointerDownRef.current) {
      return;
    }

    isDraggingRef.current = false;
    onDragActiveChange?.(false);

    if (settleClearTimeoutRef.current) {
      clearTimeout(settleClearTimeoutRef.current);
      settleClearTimeoutRef.current = null;
    }
  }, [onDragActiveChange]);

  usePlayerEventListener({
    type: 'seeked',
    handler: handleSeeked,
    location: 'VideoCommentRangeSelector',
  });

  useEffect(() => {
    return () => {
      activeBodyDragCleanupRef.current?.();
      activeBodyDragCleanupRef.current = null;

      if (settleClearTimeoutRef.current) {
        clearTimeout(settleClearTimeoutRef.current);
        settleClearTimeoutRef.current = null;
      }
      onDragActiveChange?.(false);
    };
  }, [onDragActiveChange]);

  useEffect(() => {
    if (isSelectorActive) {
      return;
    }

    activeBodyDragCleanupRef.current?.();
    activeBodyDragCleanupRef.current = null;

    if (settleClearTimeoutRef.current) {
      clearTimeout(settleClearTimeoutRef.current);
      settleClearTimeoutRef.current = null;
    }

    if (isDraggingRef.current || isPointerDownRef.current) {
      onDragActiveChange?.(false);
    }

    isDraggingRef.current = false;
    isPointerDownRef.current = false;
  }, [isSelectorActive, onDragActiveChange]);

  useEffect(() => {
    const justPaused = wasPlayingRef.current && !playing;

    wasPlayingRef.current = playing;

    if (!justPaused || !commentRange || commentRange.startTime !== commentRange.endTime) {
      return;
    }

    const snapTime = videoElement?.currentTime ?? currentTime ?? 0;

    setCommentRange({ startTime: snapTime, endTime: snapTime });
  }, [playing, commentRange, currentTime, videoElement, setCommentRange]);

  useEffect(() => {
    if (playing) {
      return;
    }

    if (isDraggingRef.current) {
      return;
    }

    if (!seeking || !commentRange) {
      return;
    }

    const t = videoElement?.currentTime ?? currentTime ?? 0;
    const { startTime, endTime } = commentRange;

    if (startTime === t && endTime === t) {
      return;
    }

    if (startTime !== endTime) {
      const min = Math.min(startTime, endTime);
      const max = Math.max(startTime, endTime);

      if (t >= min && t <= max) {
        return;
      }
    }

    setCommentRange({ startTime: t, endTime: t });
  }, [playing, seeking, commentRange, currentTime, videoElement, setCommentRange]);

  const regionStyle = useMemo<CSSProperties | undefined>(() => {
    if (!commentRange || duration <= 0) {
      return undefined;
    }

    const startPercent = (Math.min(commentRange.startTime, commentRange.endTime) / duration) * PERCENTAGE_MULTIPLIER;
    const widthPercent = (Math.abs(commentRange.endTime - commentRange.startTime) / duration) * PERCENTAGE_MULTIPLIER;

    return {
      left: `calc(${startPercent}% - ${CAP_WIDTH_PX}px)`,
      width: `calc(${widthPercent}% + ${BOTH_CAPS_PX}px)`,
    };
  }, [commentRange, duration]);

  const clearButtonSide = useMemo<CommentRangeClearButtonSide>(() => {
    if (!commentRange || duration <= 0 || !containerRect?.width) {
      return 'right';
    }

    const endTime = Math.max(commentRange.startTime, commentRange.endTime);
    const rightCapEdgePx = (endTime / duration) * containerRect.width + CAP_WIDTH_PX;
    const rightClearancePx = containerRect.width - rightCapEdgePx;

    return rightClearancePx < CLEAR_BUTTON_CLEARANCE_PX ? 'left' : 'right';
  }, [commentRange, containerRect?.width, duration]);

  const selectorTooltipLabel = useMemo(() => {
    if (!commentRange) {
      return undefined;
    }

    if (commentRange.startTime === commentRange.endTime) {
      return 'Drag a handle to select a range';
    }

    const startTime = Math.min(commentRange.startTime, commentRange.endTime);
    const endTime = Math.max(commentRange.startTime, commentRange.endTime);

    return `${formatDuration(Math.round(startTime))} - ${formatDuration(Math.round(endTime))}`;
  }, [commentRange]);

  const handleClearButtonPointerDown = useCallback((event: ReactPointerEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  }, []);

  const handleClearButtonClick = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      setCommentRange(null);
      setCommentRangeEnabled(false);
    },
    [setCommentRange, setCommentRangeEnabled],
  );

  const handleBodyPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);

      if (!commentRange) {
        return;
      }

      const container = containerRef.current;

      if (!container) {
        return;
      }

      const timelineWidth = container.getBoundingClientRect().width;

      if (timelineWidth <= 0) {
        return;
      }

      handleDragStart();
      activeBodyDragCleanupRef.current?.();

      const { startTime: initialStart, endTime: initialEnd } = commentRange;
      const rangeWidth = initialEnd - initialStart;
      const secondsPerPixel = duration / timelineWidth;
      const initialClientX = event.clientX;
      const shouldSyncPlayhead = !playing;

      if (shouldSyncPlayhead) {
        onPlayerSeekStart();
        setCurrentTime(initialStart);
        if (videoElement) {
          videoElement.currentTime = initialStart;
        }
      }

      let lastSeekTime = 0;
      let lastStart = initialStart;
      let lastRange = { startTime: initialStart, endTime: initialEnd };

      const handlePointerMove = (moveEvent: PointerEvent) => {
        const delta = (moveEvent.clientX - initialClientX) * secondsPerPixel;
        const clampedStart = Math.max(0, Math.min(initialStart + delta, duration - rangeWidth));
        const clampedEnd = clampedStart + rangeWidth;

        setCommentRange({ startTime: clampedStart, endTime: clampedEnd });
        lastStart = clampedStart;
        lastRange = { startTime: clampedStart, endTime: clampedEnd };

        if (shouldSyncPlayhead) {
          setCurrentTime(clampedStart);

          const now = performance.now();

          if (videoElement && now - lastSeekTime >= SEEK_THROTTLE_MS) {
            videoElement.currentTime = clampedStart;
            lastSeekTime = now;
          }
        }
      };

      const cleanupPointerDrag = () => {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);

        if (activeBodyDragCleanupRef.current === cleanupPointerDrag) {
          activeBodyDragCleanupRef.current = null;
        }

        if (shouldSyncPlayhead) {
          onPlayerSeekEnd();
        }
      };

      const handlePointerUp = () => {
        if (shouldSyncPlayhead && videoElement) {
          videoElement.currentTime = lastStart;
        }

        cleanupPointerDrag();
        handleDragEnd(lastRange);
      };

      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
      activeBodyDragCleanupRef.current = cleanupPointerDrag;
    },
    [
      commentRange,
      duration,
      handleDragEnd,
      handleDragStart,
      onPlayerSeekEnd,
      onPlayerSeekStart,
      playing,
      setCommentRange,
      setCurrentTime,
      videoElement,
    ],
  );

  if (!commentRange || duration <= 0) {
    return null;
  }

  return (
    <div className="pointer-events-none absolute inset-0 z-10" ref={setContainerRef}>
      <div className="group/range-selector pointer-events-none absolute -inset-y-0.5" style={regionStyle}>
        <Tooltip
          data-testid="VIDEO_COMMENT_RANGE_SELECTOR_TOOLTIP"
          disableHoverableContent
          label={selectorTooltipLabel}
          side="top"
          sideOffset={8}
        >
          <div
            className="pointer-events-auto absolute inset-0 cursor-grab overflow-hidden rounded-4 ads:border-ads-gray-12 border-gray-12 border-y-2 active:cursor-grabbing"
            data-testid="VIDEO_COMMENT_RANGE_SELECTOR"
            onPointerDown={handleBodyPointerDown}
          >
            <VideoCommentRangeSelectorHandle
              containerRef={containerRef}
              endTime={commentRange.endTime}
              onDragEnd={handleDragEnd}
              onDragStart={handleDragStart}
              side="left"
              startTime={commentRange.startTime}
            />
            <VideoCommentRangeSelectorHandle
              containerRef={containerRef}
              endTime={commentRange.endTime}
              onDragEnd={handleDragEnd}
              onDragStart={handleDragStart}
              side="right"
              startTime={commentRange.startTime}
            />
          </div>
        </Tooltip>
        <VideoCommentRangeSelectorClearButton
          onClick={handleClearButtonClick}
          onPointerDown={handleClearButtonPointerDown}
          side={clearButtonSide}
        />
      </div>
    </div>
  );
});

VideoCommentRangeSelector.displayName = 'VideoCommentRangeSelector';
